import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ChannelPhotoAvatar } from '../channel-selection/ChannelPhotoAvatar';
import { ReasonText } from '../channel-selection/ReasonText';
import type { ScreenFailure } from '../channel-selection/screen-failure';

import type { FirstChannelPick } from './pick-first-channel';

// dir="auto": a channel name in Arabic or Hebrew reads right to left (C3 R7). A constant, not a text.
const OWN_DIRECTION = 'auto';

/** What the failure of a read offers the trader (S7 R15). */
export type FailureAction = 'retry' | 'signIn';

/** Props of {@link FirstSettingsScreen}. */
export interface FirstSettingsScreenProps {
  /** The channel being set up and the count of the others; `null` while `GET /channels` loads. */
  readonly pick: FirstChannelPick | null;
  /** A skeleton labelled "Loading…" shows in the form's place (R15). */
  readonly loading: boolean;
  /** A read failed: its catalogue text and the action it offers (R15). */
  readonly failure: { readonly reason: ScreenFailure; readonly action: FailureAction } | null;
  /** M2's form, once everything it needs was read. */
  readonly form: ReactNode;
  /** After a limit already exceeded or a partial save the channel is set up: "Continue to Home" (R13). */
  readonly setUp: boolean;
  /** A save is running: "Set up later" is disabled (R17). */
  readonly busy: boolean;
  readonly onRetry: () => void;
  readonly onSignInAgain: () => void;
  readonly onLeave: () => void;
}

/**
 * Screen E5 · Step 4 of 4: first channel settings (specs/S7-first-settings/spec.md; US-06; canvas
 * `PremierReglage.html`; provisional on Q21). Presentational: no request, no navigation. It owns
 * its `main` landmark because it renders outside `AppLayout`, like E3 (S5 R15). The form below
 * the header is M2's `ChannelSettingsForm`; its "Confirm and start copying" button is its own.
 */
export function FirstSettingsScreen(props: FirstSettingsScreenProps): ReactElement {
  const { t } = useTranslation();
  const { pick, failure } = props;
  return (
    <Box
      component="main"
      sx={{
        minHeight: '100dvh',
        width: '100%',
        maxWidth: 560,
        mx: 'auto',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        px: 2,
        pt: 3,
        pb: 3.5,
      }}
    >
      <Typography sx={{ fontSize: 13, color: 'text.secondary', fontWeight: 600 }}>
        {t('onboarding.firstSettings.step')}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Typography variant="h1">{t('onboarding.firstSettings.title')}</Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: 14, lineHeight: 1.5 }}>
          {t('onboarding.firstSettings.nothingCopied')}
          {pick !== null && pick.othersToSetUp > 0
            ? ` ${t('onboarding.firstSettings.others', { count: pick.othersToSetUp })}`
            : ''}
        </Typography>
      </Box>

      {pick === null ? null : (
        <Box
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 1.75,
            py: 1.5,
            borderRadius: '14px',
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: 'background.paper',
          })}
        >
          <ChannelPhotoAvatar channel={pick.channel} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              component="span"
              dir={OWN_DIRECTION}
              sx={{ display: 'block', fontWeight: 600, overflowWrap: 'anywhere' }}
            >
              {pick.channel.title}
            </Box>
            {pick.channel.recommendedByAffiliate ? (
              <Box
                component="span"
                sx={(theme) => ({ display: 'block', fontSize: 13, color: theme.fx.textMuted })}
              >
                {t('onboarding.channels.affiliateChannel')}
              </Box>
            ) : null}
          </Box>
        </Box>
      )}

      {props.loading ? (
        <Stack spacing={1} role="status" aria-label={t('onboarding.firstSettings.loading')}>
          {[0, 1, 2].map((n) => (
            <Skeleton key={n} variant="rounded" height={96} />
          ))}
        </Stack>
      ) : null}

      {failure === null ? null : (
        <Stack spacing={1.5}>
          <ReasonText failure={failure.reason} />
          {failure.action === 'signIn' ? (
            <Button variant="contained" onClick={props.onSignInAgain}>
              {t('onboarding.channels.signInAgain')}
            </Button>
          ) : (
            <Button variant="outlined" onClick={props.onRetry}>
              {t('onboarding.channels.retry')}
            </Button>
          )}
        </Stack>
      )}

      {props.form}

      <Button
        variant="text"
        color="inherit"
        disabled={props.busy}
        onClick={props.onLeave}
        sx={(theme) => ({ minHeight: 44, color: theme.fx.textMuted, fontWeight: 600 })}
      >
        {props.setUp
          ? t('onboarding.firstSettings.continueHome')
          : t('onboarding.firstSettings.later')}
      </Button>
    </Box>
  );
}
